/* eslint-disable max-len */
import React from 'react';

import { Container } from '@components/Container';
import { Layout } from '@components/Layout';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: '運営会社',
    bannerTitle: '運営会社',
    companyInfo: '会社情報',
    locations: '本社・国内支店',
    tradeNameKeyName: '商号',
    tradeNameValue: `\
エフシースタンダードロジックス株式会社
FC STANDARD LOGISTICS CO.,LTD.`,
    locationKeyName: '本店所在地',
    locationValue: `\
〒541-0056
大阪市中央区久太郎町2丁目4番11号
クラボウアネックス ビル14階`,
    purposeKeyName: '事業目的',
    purposeValue: `\
1.一般貨物自動車運送事業
2.貨物軽自動車運送事業
3.貨物運送取扱事業
4.鉄道、船舶および航空機による国際間複合輸送業務
5.物流情報の収集、処理ならびに販売に関する業務
6.輸出入手続の事務代行業
7.損害保険代理店業
8.通関業
9.前各号に附帯する一切の業務
平成19年11月1日変更  平成19年11月30日登記`,
    startDateKeyName: '創業・設立',
    startDateValue: `\
創業  2000年5月1日
設立  2006年3月6日`,
    capitalKeyName: '資本金',
    capitalValue: '50,000,000円 (平成23年8月30日現在)',
    fiscalsKeyName: '決算',
    fiscalsValue: '年1回 (3月)',
    mainBanksKeyName: '主要取引銀行',
    mainBanksValue: `\
株式会社りそな銀行
株式会社三井住友銀行`,
    mainLocationKeyName: '本社',
    mainLocationValue: `\
〒541-0056
大阪市中央区久太郎町2丁目4番11号
クラボウアネックス ビル14階
TEL 06-6266-8867
FAX 06-6266-8566
最寄り駅
堺筋本町駅より徒歩1分
`,
    branchLocationKeyName: '東京支店',
    branchLocationValue: `\
〒104-0045
東京都中央区築地7丁目5番3号
紀文第一ビル4階
TEL 03-5565-8818
FAX 03-5565-7855
最寄り駅
日比谷線築地駅より徒歩5分`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  inner: {
    ...common.verticalSpace4,
    backgroundColor: 'transparent',
    padding: '0 0',
  },
  title: {
    ...common.normalText,
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 30,
    lineHeight: '42px',
    marginBottom: '3rem',
  },
  content: {
    ...common.normalText,
    whiteSpace: 'pre-wrap',
    '& b': {
      color: theme.palette.grey['800'],
    },
    color: theme.palette.grey['700'],
  },
  section: {
    ...common.flexColumn,
    backgroundColor: theme.palette.background.paper,
    padding: '1rem',
    [theme.breakpoints.up('lg')]: {
      padding: '2rem 4rem',
    },
  },
  aboutRowContainer: {
    ...common.normalText,
    [theme.breakpoints.up('lg')]: {
      ...common.flexInline,
    },
  },
  aboutRowCell: {
    ...common.flexColumn,
    alignItems: 'flex-start',
    justifyContent: 'center',
    color: theme.palette.grey['700'],
    flex: 2,
    whiteSpace: 'pre-wrap',
    padding: '1rem 0',
    lineHeight: '22.4px',
  },
  aboutRowKey: {
    color: theme.palette.common.black,
    flex: 1,
  },
  aboutRows: {
    '& > *:not(:last-child)': {
      borderBottomStyle: 'solid',
      borderBottomWidth: 1,
      borderBottomColor: theme.palette.grey[200],
    },
    '& a:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  locationContainer: {
    ...common.flexColumn,
    [theme.breakpoints.up('lg')]: {
      ...common.flexInline,
    },
  },
  locationTexts: {
    ...common.flexColumn,
    flex: 1,
    marginBottom: '1rem',
    [theme.breakpoints.up('lg')]: {
      marginBottom: 0,
      minWidth: 'calc(100% - 500px)',
    },
  },
  locationTitle: {
    ...common.biggerText,
    fontWeight: 500,
    marginBottom: '2rem',
  },
  locationContent: {
    ...common.normalText,
    whiteSpace: 'pre-wrap',
    lineHeight: '30px',
    color: theme.palette.grey['700'],
  },
}));

export interface AboutRowProps {
  keyName: string;
  value: React.ReactNode;
}

export const AboutRow: React.FC<AboutRowProps> = (props) => {
  const { keyName, value } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.aboutRowContainer}>
      <div className={clsx(classes.aboutRowCell, classes.aboutRowKey)}>{keyName}</div>
      <div className={clsx(classes.aboutRowCell)}>{value}</div>
    </div>
  );
};

export interface LocationProps {
  title: string;
  location: string;
  mapUrl: string;
}

export const Location: React.FC<LocationProps> = (props) => {
  const { title, location, mapUrl: image } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.locationContainer}>
      <div className={classes.locationTexts}>
        <div className={classes.locationTitle}>{title}</div>

        <div className={classes.locationContent}>{location}</div>
      </div>
      <iframe
        title="Google Map"
        src={image}
        style={{
          border: 0,
          width: '100%',
          height: 315,
        }}
        allowFullScreen={false}
        loading="lazy"
      />
    </div>
  );
};

export const AboutCompany: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Layout title={strings.title} breadcrumbs={['/utilities/Dashboard', '/about/AboutCompany']}>
      <Container classes={{ inner: classes.inner }}>
        <div className={classes.section}>
          <div className={classes.title}>{strings.companyInfo}</div>

          <div className={classes.aboutRows}>
            <AboutRow
              keyName={strings.tradeNameKeyName}
              value={
                <a href="https://fcstandard.com/main/" target="_blank" rel="noreferrer">
                  {strings.tradeNameValue}
                </a>
              }
            />

            <AboutRow keyName={strings.locationKeyName} value={strings.locationValue} />

            <AboutRow keyName={strings.purposeKeyName} value={strings.purposeValue} />

            <AboutRow keyName={strings.startDateKeyName} value={strings.startDateValue} />

            <AboutRow keyName={strings.capitalKeyName} value={strings.capitalValue} />

            <AboutRow keyName={strings.fiscalsKeyName} value={strings.fiscalsValue} />

            <AboutRow keyName={strings.mainBanksKeyName} value={strings.mainBanksValue} />
          </div>
        </div>

        <div className={classes.section}>
          <div className={classes.title}>{strings.locations}</div>
          <div className={clsx(classes.flexColumn, classes.verticalSpace4)}>
            <Location
              title={strings.mainLocationKeyName}
              location={strings.mainLocationValue}
              mapUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3280.960527206482!2d135.50295991564096!3d34.680945691782995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6000e718c7ee2a1d%3A0x9757a0000a29ab9b!2z5pel5pys44CB44CSNTQxLTAwNTYg5aSn6Ziq5bqc5aSn6Ziq5biC5Lit5aSu5Yy65LmF5aSq6YOO55S677yS5LiB55uu77yU4oiS77yR77yR!5e0!3m2!1sja!2sus!4v1625042686582!5m2!1sja!2sus"
            />

            <Location
              title={strings.branchLocationKeyName}
              location={strings.branchLocationValue}
              mapUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.4474713014447!2d139.7714908156583!3d35.665982438414744!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601889621d72b8c1%3A0x634cff7c6e973159!2z5pel5pys44CB44CSMTA0LTAwNDUg5p2x5Lqs6YO95Lit5aSu5Yy656-J5Zyw77yX5LiB55uu77yV4oiS77yT!5e0!3m2!1sja!2sus!4v1625042733725!5m2!1sja!2sus"
            />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.bannerTitle,
  order: 4,
};
